import type { SupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/TaxInfoGatheringViewModel'
import { observer } from 'mobx-react-lite'
import {
  ChecklistStatusCard,
  ChecklistStatusCardItemList,
  ChecklistStatusCardListItem,
  ChecklistStatusCardSkeleton,
} from '@/features/jobs/details/tax-type/info-gathering/components/ChecklistStatusCard/index'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { matchObj } from '@taxfyle/backend-commons/lib/utils/pattern-match-util'

interface ChecklistStatusCardContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Container for the checklist status card.
 */
export const ChecklistStatusCardContainer = observer(
  function ChecklistStatusCardContainer({
    vm,
  }: ChecklistStatusCardContainerProps) {
    if (vm.infoGatheringVm.fetchChecklist.pending) {
      return <ChecklistStatusCardSkeleton />
    }

    return matchObj(vm.infoGatheringVm.completion, 'type', {
      Empty: () => null,
      Incomplete: ({ incomplete, completed }) => (
        <ChecklistStatusCard
          onOpenChecklistPress={vm.showOpenItemsDialog}
          state="incomplete"
        >
          <ChecklistStatusCardItemList
            title="Missing"
            testId="checklist-missing-section"
          >
            {incomplete.map((item, idx) => (
              <ChecklistStatusCardListItem
                key={idx}
                text={formatItemName(item)}
                isCompleted={false}
                testId={`checklist-item-${getItemTestIdKey(item)}`}
              />
            ))}
          </ChecklistStatusCardItemList>
          {completed.length > 0 && (
            <ChecklistStatusCardItemList
              title="Completed"
              testId="checklist-completed-section"
            >
              {completed.map((item, idx) => (
                <ChecklistStatusCardListItem
                  key={idx}
                  text={formatItemName(item)}
                  isCompleted
                  testId={`checklist-item-${getItemTestIdKey(item)}`}
                />
              ))}
            </ChecklistStatusCardItemList>
          )}
        </ChecklistStatusCard>
      ),
      Completed: ({ items }) => (
        <ChecklistStatusCard
          onOpenChecklistPress={vm.showOpenItemsDialog}
          state="complete"
        >
          <ChecklistStatusCardItemList
            title="Completed"
            testId="checklist-completed-section"
          >
            {items.map((item, idx) => (
              <ChecklistStatusCardListItem
                key={idx}
                text={formatItemName(item)}
                isCompleted
                testId={`checklist-item-${getItemTestIdKey(item)}`}
              />
            ))}
          </ChecklistStatusCardItemList>
        </ChecklistStatusCard>
      ),
    })
  },
)

function formatItemName(vm: SupportingItemViewModel) {
  switch (vm.type) {
    case 'PersonalInfo':
      return 'Personal information'
    case 'SpousalInfo':
      return 'Spousal information'
    case 'DependentsInfo':
      return 'Dependents information'
    case 'BusinessInfo':
      return 'Business information'
    case 'PersonalTaxRefundMethod':
      return 'Personal tax refund method'
    case 'BusinessTaxRefundMethod':
      return 'Business tax refund method'
    case 'Document':
      return vm.item.name
    default:
      return 'Unknown item'
  }
}

function getItemTestIdKey(vm: SupportingItemViewModel) {
  switch (vm.type) {
    case 'PersonalInfo':
      return 'personal-info'
    case 'SpousalInfo':
      return 'spousal-info'
    case 'DependentsInfo':
      return 'dependents-info'
    case 'BusinessInfo':
      return 'business-info'
    case 'PersonalTaxRefundMethod':
      return 'personal-tax-refund-method'
    case 'BusinessTaxRefundMethod':
      return 'business-tax-refund-method'
    case 'Document':
      return `document-${vm.item.input.documentTypeShortId}`
    default:
      return 'unknown-item'
  }
}
